import { useCallback, useEffect, useRef, useState } from 'react'
import { BREAKS } from '../content/data'

export type TimerPhase = 'work' | 'break'

/** Tek uyarı mı, susturana kadar mı çalsın. */
export type AlarmMode = 'once' | 'until_stopped'

interface Options {
  workMinutes: number
  breakMinutes: number
  soundOn: boolean
  alarmMode: AlarmMode
  autoContinue: boolean
  onCycleComplete: (cycles: number, standSeconds: number) => void
  initialCycles: number
  initialStandSeconds: number
}

const now = () => Date.now()

/** Tekrarlı alarmda iki çalış arası. 3 sn: fark edilir ama telaşlı değil. */
const ALARM_INTERVAL_MS = 3000

/**
 * Tekrarlı alarmın üst sınırı.
 *
 * Kullanıcı "ben kapatana kadar çalsın" istedi ve haklı — tek bip'i gürültülü
 * ortamda kaçırıyor. Ama masadan kalkıp gittiyse alarmı susturacak kimse yok
 * ve panel sonsuza kadar öten bir sekmeye dönüşür. 5 dakika hem "duyana kadar"
 * için fazlasıyla yeterli hem de terk edilmiş bir sekme için katlanılabilir.
 */
const ALARM_MAX_MS = 5 * 60 * 1000

/**
 * Mola sayacı — bilerek tamamen istemcide.
 * Sunucuya taşımak yanlış olurdu: durumlu (başlat/duraklat/atla), etkileşimli ve
 * kullanıcının ne zaman çalışmaya oturduğuna bağlı. Sunucu bunu bilemez.
 *
 * Kalan süre saniye sayarak DEĞİL, bitiş zaman damgasından hesaplanıyor.
 * Sebep: tarayıcı arka plan sekmesindeyken setInterval dakikada bire kadar
 * kısılıyor. Sayaç azaltsaydı 50 dakikalık blok 70 dakika sürerdi ve kimse
 * neden geciktiğini anlamazdı. Zaman damgası bu kısıtlamadan etkilenmiyor.
 *
 * State'in kendisi TimerProvider'da yaşıyor — bileşen sekme değişince
 * sökülünce sayaç sıfırlanmasın diye.
 */
export function useBreakTimer(o: Options) {
  const [phase, setPhase] = useState<TimerPhase>('work')
  const [endsAt, setEndsAt] = useState<number | null>(null)      // null = duraklatılmış
  const [pausedLeft, setPausedLeft] = useState(o.workMinutes * 60)
  const [tick, setTick] = useState(0)                            // yeniden çizim tetikleyici
  const [cycles, setCycles] = useState(o.initialCycles)
  const [standSeconds, setStand] = useState(o.initialStandSeconds)
  const [breakIdx, setBreakIdx] = useState(0)

  const [ringing, setRinging] = useState(false)

  const opts = useRef(o); opts.current = o
  const lastTick = useRef(now())
  const blinkId = useRef<number | null>(null)
  const audio = useRef<AudioContext | null>(null)
  const alarmId = useRef<number | null>(null)
  const alarmStop = useRef<number | null>(null)

  const running = endsAt !== null
  const left = running ? Math.max(0, Math.ceil((endsAt - now()) / 1000)) : pausedLeft
  void tick // left'in her tick'te yeniden hesaplanması için

  // ── bildirim kanalları ────────────────────────────────────────────────────
  /**
   * Ses tek bir AudioContext üzerinden çalıyor.
   *
   * Önceden her uyarıda yeni bir context açılıyordu. Tek bip için sorun değildi
   * ama tekrarlı alarmda dakikada 20 context demek — tarayıcılar bunu bir yerden
   * sonra reddediyor. Ayrıca context'i kullanıcı hareketiyle (Başlat tuşu) bir kez
   * açıp canlı tutmak, sonraki seslerin "otomatik oynatma" engeline takılmasını
   * da önlüyor.
   */
  const ctx = useCallback(() => {
    if (!audio.current) {
      const C = window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      audio.current = new C()
    }
    if (audio.current.state === 'suspended') void audio.current.resume()
    return audio.current
  }, [])

  const beep = useCallback((kind: 'start' | 'break') => {
    if (!opts.current.soundOn) return
    try {
      const c = ctx()
      const seq = kind === 'break' ? [880, 660, 880] : [523, 659, 784]
      seq.forEach((f, i) => {
        const osc = c.createOscillator(); const gain = c.createGain()
        osc.type = 'sine'; osc.frequency.value = f
        const t = c.currentTime + i * 0.22
        gain.gain.setValueAtTime(0.0001, t)
        gain.gain.exponentialRampToValueAtTime(0.25, t + 0.03)
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.2)
        osc.connect(gain); gain.connect(c.destination)
        osc.start(t); osc.stop(t + 0.22)
      })
    } catch { /* ses yoksa sessizce devam */ }
  }, [ctx])

  const stopAlarm = useCallback(() => {
    if (alarmId.current) { window.clearInterval(alarmId.current); alarmId.current = null }
    if (alarmStop.current) { window.clearTimeout(alarmStop.current); alarmStop.current = null }
    setRinging(false)
  }, [])

  /** Tek bip ya da susturulana kadar tekrar — kullanıcının seçimine göre. */
  const alarm = useCallback((kind: 'start' | 'break') => {
    stopAlarm()
    beep(kind)
    if (!opts.current.soundOn || opts.current.alarmMode !== 'until_stopped') return
    setRinging(true)
    alarmId.current = window.setInterval(() => beep(kind), ALARM_INTERVAL_MS)
    alarmStop.current = window.setTimeout(stopAlarm, ALARM_MAX_MS)
  }, [beep, stopAlarm])

  // Bileşen sökülürse alarm arkada çalmaya devam etmesin.
  useEffect(() => stopAlarm, [stopAlarm])

  /** Sekme arka plandayken başlığı yanıp söndür — bildirimden bağımsız, her yerde çalışır. */
  const blink = useCallback((text: string) => {
    if (blinkId.current) window.clearInterval(blinkId.current)
    const base = 'Kilo Takip Paneli'
    let on = false, n = 0
    blinkId.current = window.setInterval(() => {
      document.title = (on = !on) ? text : base
      if (++n > 40) { if (blinkId.current) window.clearInterval(blinkId.current); document.title = base }
    }, 700)
    window.addEventListener('focus', () => {
      if (blinkId.current) window.clearInterval(blinkId.current)
      document.title = base
    }, { once: true })
  }, [])

  const notify = useCallback((title: string, body: string) => {
    try {
      if ('Notification' in window && Notification.permission === 'granted')
        new Notification(title, { body, tag: 'kilo-timer' })
    } catch { /* yok say */ }
  }, [])

  // ── faz geçişi ────────────────────────────────────────────────────────────
  const advance = useCallback(() => {
    setPhase((cur) => {
      if (cur === 'work') {
        const idx = Math.floor(Math.random() * BREAKS.length)
        setBreakIdx(idx)
        const secs = opts.current.breakMinutes * 60
        setPausedLeft(secs)
        setEndsAt(now() + secs * 1000)
        alarm('break'); blink('⏰ MOLA — kalk!')
        notify('Mola vakti — kalk', `${BREAKS[idx][0]} · ${BREAKS[idx][1]}`)
        return 'break'
      }

      const secs = opts.current.workMinutes * 60
      setPausedLeft(secs)
      setEndsAt(opts.current.autoContinue ? now() + secs * 1000 : null)
      setCycles((c) => {
        const next = c + 1
        setStand((s) => { opts.current.onCycleComplete(next, s); return s })
        return next
      })
      alarm('start'); blink('▶ Çalışmaya dön')
      notify('Mola bitti', 'Çalışmaya dön.')
      return 'work'
    })
  }, [alarm, blink, notify])

  // ── saat ──────────────────────────────────────────────────────────────────
  // 250ms: geri sayımın takılmadan görünmesi için yeterli, pil için önemsiz.
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = now()
      const dt = (t - lastTick.current) / 1000
      lastTick.current = t

      if (endsAt !== null) {
        if (phase === 'break') setStand((s) => s + dt)
        if (t >= endsAt) advance()
      }
      setTick((n) => n + 1)
    }, 250)
    return () => window.clearInterval(id)
  }, [endsAt, phase, advance])

  // Sunucudan gelen sayaçlar geç yüklenebiliyor; büyük olanı al.
  // (Bu alanlar gün içinde monoton artıyor, geri gitmemeli.)
  useEffect(() => {
    setCycles((c) => Math.max(c, o.initialCycles))
    setStand((s) => Math.max(s, o.initialStandSeconds))
  }, [o.initialCycles, o.initialStandSeconds])

  // Başlat/Duraklat/Sıfırla/Atla — hepsi "gördüm" demek. Alarm burada susuyor;
  // ayrıca ilk dokunuşta ses motorunu açıyoruz ki sonraki alarmlar engellenmesin.
  const toggle = useCallback(() => {
    stopAlarm()
    try { ctx() } catch { /* ses yoksa sessizce devam */ }
    setEndsAt((e) => {
      if (e !== null) { setPausedLeft(Math.max(0, Math.ceil((e - now()) / 1000))); return null }
      return now() + pausedLeft * 1000
    })
  }, [pausedLeft, stopAlarm, ctx])

  const reset = useCallback(() => {
    stopAlarm()
    setPhase('work')
    setEndsAt(null)
    setPausedLeft(opts.current.workMinutes * 60)
  }, [stopAlarm])

  const skip = useCallback(() => { stopAlarm(); advance() }, [stopAlarm, advance])

  const total = (phase === 'work' ? o.workMinutes : o.breakMinutes) * 60

  return {
    phase, left, running, cycles,
    standSeconds: Math.round(standSeconds),
    progress: total > 0 ? 1 - left / total : 0,
    breakTask: BREAKS[breakIdx],
    /** Alarm şu anda çalıyor mu — arayüz "Sustur" düğmesini buna göre gösteriyor. */
    ringing,
    stopAlarm,
    toggle,
    skip,
    reset,
  }
}
